import {
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import posthog from "posthog-js";
import type { OnboardingProgress, OnboardingStep } from "@/types";

export interface OnboardingStepConfig {
  id: OnboardingStep;
  title: string;
  description: string;
  actionText: string;
  route?: string; // Optional route to navigate to
  icon: string; // Lucide icon name
  optional?: boolean; // If true, doesn't block onboarding completion
}

export const ONBOARDING_STEPS: OnboardingStepConfig[] = [
  {
    id: "createOrJoinTeam",
    title: "Create or join a team",
    description:
      "Teams are where your work lives and where collaboration happens.",
    actionText: "Go to teams",
    route: "teams",
    icon: "Users",
  },
  {
    id: "createProject",
    title: "Create or join a project",
    description: "Projects give your work context and help track what matters.",
    actionText: "Go to projects",
    route: "projects",
    icon: "FolderOpen",
  },
  {
    id: "createUpdate",
    title: "Share your first update",
    description:
      "Let your work speak. Share progress, decisions, or learnings.",
    actionText: "Post an update",
    route: "feed",
    icon: "MessageSquare",
  },
  {
    id: "inviteTeamMember",
    title: "Invite a teammate",
    description:
      "Work is better together. Bring someone along for the journey.",
    actionText: "Invite someone",
    route: "settings/members",
    icon: "UserPlus",
  },
  {
    id: "exploreIntegrations",
    title: "Connect your tools",
    description:
      "Get notified in Slack when updates are shared, or post from Figma.",
    actionText: "Explore integrations",
    route: "settings?tab=integrations",
    icon: "Plug",
    optional: true,
  },
];

/**
 * Initialize onboarding for a new user
 */
export async function initializeOnboarding(userId: string): Promise<void> {
  const userRef = doc(db, "users", userId);

  const onboardingData: OnboardingProgress = {
    completed: false,
    currentStep: "createOrJoinTeam",
    steps: {
      createOrJoinTeam: false,
      inviteTeamMember: false,
      createProject: false,
      createUpdate: false,
      exploreIntegrations: false,
    },
    startedAt: serverTimestamp() as any,
  };

  await setDoc(
    userRef,
    {
      onboarding: onboardingData,
    },
    { merge: true }
  );

  // Track onboarding start in PostHog
  if (typeof window !== "undefined" && posthog.__loaded) {
    posthog.capture("onboarding_started", {
      user_id: userId,
    });
  }
}

/**
 * Mark a specific onboarding step as complete
 */
export async function completeOnboardingStep(
  userId: string,
  step: OnboardingStep
): Promise<void> {
  const userRef = doc(db, "users", userId);
  const userDoc = await getDoc(userRef);
  const userData = userDoc.data();
  const onboarding = userData?.onboarding as OnboardingProgress | undefined;

  if (!onboarding) {
    // Initialize onboarding if it doesn't exist
    await initializeOnboarding(userId);
  }

  // Update the specific step
  await updateDoc(userRef, {
    [`onboarding.steps.${step}`]: true,
  });

  // Track step completion in PostHog
  if (typeof window !== "undefined" && posthog.__loaded) {
    posthog.capture("onboarding_step_completed", {
      user_id: userId,
      step,
    });
  }

  // Check if all steps are complete
  const updatedDoc = await getDoc(userRef);
  const updatedData = updatedDoc.data();
  const updatedOnboarding = updatedData?.onboarding as OnboardingProgress;

  if (updatedOnboarding && areAllStepsComplete(updatedOnboarding.steps)) {
    await completeOnboarding(userId);
  } else {
    // Move to next incomplete step
    const nextStep = getNextIncompleteStep(updatedOnboarding?.steps);
    if (nextStep) {
      await updateDoc(userRef, {
        "onboarding.currentStep": nextStep,
      });
    }
  }
}

/**
 * Mark onboarding as fully complete
 */
export async function completeOnboarding(userId: string): Promise<void> {
  const userRef = doc(db, "users", userId);

  await updateDoc(userRef, {
    "onboarding.completed": true,
    "onboarding.completedAt": serverTimestamp(),
  });

  // Track onboarding completion in PostHog
  if (typeof window !== "undefined" && posthog.__loaded) {
    posthog.capture("onboarding_completed", {
      user_id: userId,
    });
  }
}

/**
 * Skip onboarding
 */
export async function skipOnboarding(userId: string): Promise<void> {
  const userRef = doc(db, "users", userId);

  await updateDoc(userRef, {
    "onboarding.skipped": true,
    "onboarding.completed": true,
    "onboarding.completedAt": serverTimestamp(),
  });

  // Track onboarding skip in PostHog
  if (typeof window !== "undefined" && posthog.__loaded) {
    posthog.capture("onboarding_skipped", {
      user_id: userId,
    });
  }
}

/**
 * Resume onboarding (undo skip)
 */
export async function resumeOnboarding(userId: string): Promise<void> {
  const userRef = doc(db, "users", userId);

  await updateDoc(userRef, {
    "onboarding.skipped": false,
    "onboarding.completed": false,
  });

  // Track onboarding resume in PostHog
  if (typeof window !== "undefined" && posthog.__loaded) {
    posthog.capture("onboarding_resumed", {
      user_id: userId,
    });
  }
}

/**
 * Get user's onboarding progress
 */
export async function getOnboardingProgress(
  userId: string
): Promise<OnboardingProgress | null> {
  const userRef = doc(db, "users", userId);
  const userDoc = await getDoc(userRef);
  const userData = userDoc.data();

  return userData?.onboarding || null;
}

/**
 * Get required (non-optional) steps
 */
const REQUIRED_STEPS = ONBOARDING_STEPS.filter((s) => !s.optional).map(
  (s) => s.id
);

/**
 * Check if all required steps are complete (optional steps don't block completion)
 */
function areAllStepsComplete(steps: OnboardingProgress["steps"]): boolean {
  return REQUIRED_STEPS.every((stepId) => steps[stepId] === true);
}

/**
 * Get the next incomplete step (only considers required steps for auto-advancement)
 */
function getNextIncompleteStep(
  steps?: OnboardingProgress["steps"]
): OnboardingStep | null {
  if (!steps) return "createOrJoinTeam";

  // Only auto-advance through required steps
  const stepOrder: OnboardingStep[] = [
    "createOrJoinTeam",
    "createProject",
    "createUpdate",
    "inviteTeamMember",
  ];

  for (const step of stepOrder) {
    if (!steps[step]) {
      return step;
    }
  }

  // Don't auto-advance to optional steps - return null when required are done
  return null;
}

/**
 * Get completion percentage (0-100) - only counts required steps
 */
export function getOnboardingCompletionPercentage(
  onboarding?: OnboardingProgress
): number {
  if (!onboarding) return 0;
  if (onboarding.completed) return 100;

  const completedRequired = REQUIRED_STEPS.filter(
    (stepId) => onboarding.steps[stepId] === true
  ).length;

  return Math.round((completedRequired / REQUIRED_STEPS.length) * 100);
}

/**
 * Get the current step configuration
 */
export function getCurrentStepConfig(
  onboarding?: OnboardingProgress
): OnboardingStepConfig | null {
  if (!onboarding || !onboarding.currentStep) {
    return ONBOARDING_STEPS[0];
  }

  return ONBOARDING_STEPS.find((s) => s.id === onboarding.currentStep) || null;
}

/**
 * Get all incomplete steps
 */
export function getIncompleteSteps(
  onboarding?: OnboardingProgress
): OnboardingStepConfig[] {
  if (!onboarding) return ONBOARDING_STEPS;

  return ONBOARDING_STEPS.filter((step) => !onboarding.steps[step.id]);
}

/**
 * Check if user should see onboarding
 */
export function shouldShowOnboarding(onboarding?: OnboardingProgress): boolean {
  if (!onboarding) return true;
  if (onboarding.completed) return false;
  if (onboarding.skipped) return false;

  return true;
}
